import { useState, useRef, useEffect, useMemo } from 'react';
import { Input } from './ui/input';
import { MapPin, Plus, Check } from 'lucide-react';

/**
 * RegionCombobox — type-first, creatable region picker.
 * As the admin types, previously-saved regions surface (closest match first).
 * Click a suggestion to reuse it, or just keep typing to save a brand-new region.
 */
export default function RegionCombobox({ value, onChange, regions = [], placeholder = 'e.g. Region IX' }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const q = (value || '').trim().toLowerCase();

  // Rank: exact > startsWith > substring > the rest (alpha). Closest-match first.
  const ranked = useMemo(() => {
    const score = (r) => {
      const s = r.toLowerCase();
      if (!q) return 3;
      if (s === q) return 0;
      if (s.startsWith(q)) return 1;
      if (s.includes(q)) return 2;
      return 99;
    };
    return [...regions]
      .map(r => ({ r, sc: score(r) }))
      .filter(x => x.sc < 99)
      .sort((a, b) => (a.sc - b.sc) || a.r.toLowerCase().localeCompare(b.r.toLowerCase()))
      .map(x => x.r);
  }, [regions, q]);

  const exactExists = regions.some(r => r.trim().toLowerCase() === q && q);
  const showCreate = q && !exactExists;

  const pick = (r) => { onChange(r); setOpen(false); };

  return (
    <div className="relative" ref={wrapRef} data-testid="region-combobox">
      <div className="relative">
        <MapPin size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid="branch-region-input"
          className="pl-8"
          value={value || ''}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
        />
      </div>
      {open && (ranked.length > 0 || showCreate) && (
        <div className="absolute z-50 mt-1 w-full max-h-52 overflow-y-auto rounded-md border bg-white shadow-lg"
          style={{ borderColor: 'var(--border-default)' }} data-testid="region-suggestions">
          {ranked.map(r => (
            <button key={r} type="button"
              onClick={() => pick(r)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50"
              data-testid={`region-option-${r}`}>
              <MapPin size={13} className="text-slate-400 shrink-0" />
              <span className="flex-1 truncate">{r}</span>
              {(value || '').trim().toLowerCase() === r.toLowerCase() && <Check size={14} className="text-[#1A4D2E]" />}
            </button>
          ))}
          {showCreate && (
            <button type="button"
              onClick={() => pick((value || '').trim())}
              className="flex w-full items-center gap-2 border-t px-3 py-1.5 text-left text-sm text-[#1A4D2E] hover:bg-emerald-50"
              style={{ borderColor: 'var(--border-default)' }}
              data-testid="region-create-option">
              <Plus size={13} className="shrink-0" />
              <span>Save new region “<span className="font-semibold">{(value || '').trim()}</span>”</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
